<template>
  <v-card>
    <v-card-title
      class="d-flex justify-space-between align-center px-6 px-lg-10 py-6"
    >
      <h2 class="ma-0 text-bold font-28 primary--text">{{ title }}</h2>
      <v-btn icon class="btn-close" aria-label="Close" @click="cancel">
        <img src="@/assets/icons/ic_close.svg" />
      </v-btn>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text
      :style="`height: ${pslScrollHeight}`"
      class="pl-6 pl-lg-10 pr-1 pr-lg-5 py-6"
    >
      <v-alert
        color="var(--blue-100)"
        class="mb-6 border-sm border-solid border-info"
      >
        <div class="d-flex flex-no-wrap">
          <div class="mr-3">
            <v-icon color="var(--blue-500)">mdi-information</v-icon>
          </div>
          <div class="black2-color">
            <span class="font-weight-bold">Important!</span>
            {{ inforMsg }}
          </div>
        </div>
      </v-alert>
      <ul>
        <li
          class="d-flex"
          v-for="(eligibility, index) in listEligibility"
          :key="index"
        >
          <span class="flex-shrink-0 white--text">{{ eligibility?.id }}</span>
          <div>
            <p class="primary--text">
              {{ eligibility?.text }}
            </p>
            <div>
              <a
                class="action__item"
                v-if="
                  eligibility?.expandContent && indexOfContentExpand !== index
                "
                @click="expandContent(index)"
                >{{ eligibility?.eventTextDisplayOpen }}
                <v-icon v-if="eligibility?.iconOpen">{{
                  eligibility?.iconOpen
                }}</v-icon></a
              >
            </div>
            <div
              v-if="indexOfContentExpand === index"
              v-html="eligibility?.expandContent"
            ></div>
            <div>
              <a
                class="action__item"
                v-if="
                  eligibility?.expandContent && indexOfContentExpand === index
                "
                @click="closeExpandContent()"
                >{{ eligibility?.eventTextDisplayClose }}
                <v-icon v-if="eligibility?.iconClose">{{
                  eligibility?.iconClose
                }}</v-icon></a
              >
            </div>
          </div>
        </li>
      </ul>
    </v-card-text>

    <v-divider></v-divider>

    <v-card-actions class="px-6 px-lg-10 py-6">
      <div class="d-flex flex-column justify-center align-center flex-grow-1">
        <BaseButton
          text="CONFIRM ELIGIBILITY"
          rounded
          depressed
          height="60"
          class="font-weight-bold font-20 mb-4"
          :color="'psorange'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '420' : '100%'"
          @click="handleConfirmEligibility"
        >
        </BaseButton>

        <a
          @click="handleEligibilityNotMet"
          class="font-18 text-decoration-underline"
          >Don't meet eligibility criteria?</a
        >
      </div>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { quoteResponseToState } from "@/common/quotes";
import { PRICED } from "@/constants/common.constants";
import {
  QUOTE_ELIGIBILITY,
  QUOTE_ELIGIBILITY_INFO_MSG,
} from "@/constants/eligibility.constants";
import {
  DIALOG_MODULE,
  QUESTIONS_MODULE,
  STEPS_MODULE,
} from "@/store/types/module-types";
import BaseButton from "@/components/BaseComponents/BaseButton.vue";

export default {
  name: "Eligibility",
  components: {
    BaseButton,
  },
  props: {
    cancel: {
      type: Function,
      required: true,
    },
    dialogRef: {
      type: Object,
      required: true,
    },
    setDefaultUnapprovedMortgageApplication: {
      type: Boolean,
      default: false,
    },
    continuationUrl: {
      type: String,
    },
    dialogLoading: {
      type: Object,
      required: true,
    },
    dialogNotMet: {
      type: Object,
      required: true,
    },
    handleNextData: {
      type: Object,
      required: false,
    },
    title: {
      type: String,
      required: true,
    },
    isShow: {
      type: Boolean,
      required: false,
    },
    inforMsg: {
      type: String,
      required: false,
      default: QUOTE_ELIGIBILITY_INFO_MSG,
    },
    listEligibility: {
      type: Array,
      required: false,
      default() {
        return QUOTE_ELIGIBILITY;
      },
    },
    customHandleConfirmEligibility: {
      type: Boolean,
      required: false,
      default: false,
    },
    pslScrollHeight: {
      type: String,
      default: "auto",
    },
  },
  data() {
    return {
      indexOfContentExpand: -1,
    };
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, ["getQuoteRequestIdAfterRetrieve"]),
  },
  methods: {
    ...mapActions(STEPS_MODULE, ["handleNext"]),
    ...mapMutations(DIALOG_MODULE, ["showModal"]),
    ...mapActions(QUESTIONS_MODULE, [
      "getQuotes",
      "cloneQuote",
      "setIsExpiredFlag",
      "setHomeEligibility",
      "setIsAnnualRequote",
      "setRequireAnnualRequoteConfirmation",
    ]),
    async handleConfirmEligibility() {
      if (this.continuationUrl) {
        this.dialogRef.cancel();
        window.location.href = this.continuationUrl;
        return;
      }

      if (this.customHandleConfirmEligibility === true) {
        this.$emit("confirmEligibility");
        this.dialogRef.cancel();
        return;
      }

      this.dialogRef.cancel();
      this.dialogLoading.open("Hang tight, getting quotes...");
      const quoteRequestFields = {
        IsPartialQuote: false,
        SetDefaultUnapprovedMortgageApplication:
          this.setDefaultUnapprovedMortgageApplication,
      };
      this.getQuotes(quoteRequestFields)
        .then((quoteResponse) => {
          this.dialogLoading.cancel();
          const newState = quoteResponseToState(quoteResponse);
          this.setIsExpiredFlag(false);
          if (newState === PRICED) {
            // Go forward if it got prices
            // Otherwise (UNABLETOQUOTE, etc.) stay on page to show error bars
            this.handleNext(this.handleNextData);
            // isExpired is always false once a new quote is obtained.
            this.setIsExpiredFlag(false);
          } else {
            // If the quote was impossible, it's now read-only in the API side
            // and we need to create a clone for the user to fix and re-submit
            this.cloneQuote();
          }
        })
        .catch(() => {
          this.dialogLoading.cancel();
        });
    },
    handleEligibilityNotMet() {
      this.dialogRef.cancel();
      this.dialogNotMet.open("Eligibility check");
    },
    expandContent(index) {
      this.indexOfContentExpand = index;
    },
    closeExpandContent() {
      this.indexOfContentExpand = -1;
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.v-card__title) {
  h2 {
    max-width: 80%;
    word-break: break-word;
  }
}

:deep(.v-card__text) {
  ul {
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      margin-bottom: 31px;

      &:last-child {
        margin-bottom: 0;
      }

      span {
        background: var(--v-accent-base);
        border-radius: 50%;
        font-size: 18px;
        font-weight: 800;
        height: 28px;
        margin-right: 24px;
        width: 28px;

        display: flex;
        justify-content: center;
        align-items: center;
      }
      p {
        margin: 0;
      }
      .action__item {
        color: #106175;
        .v-icon {
          color: #106175;
        }
      }
    }
  }
}
</style>
